import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { Dimensions, NativeModules, Platform, View } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';
import AppRoutes from './app';
import history from './history';

import AppLocale from '@common/configs/translation';
import { appLanguageSelector } from '@common/redux/selectors/language';
import { setLanguage } from '@common/redux/slice/language';
import {
  setDimensionStore,
  setLoadingDimension,
} from '@common/redux/slice/page';
import FCMComponent from '@common/utils/FCM/FCMComponent';
import { desktopWidth } from '@common/constants/shared';
import useNetworkStatus from '@common/hooks/useNetworkStatus';
import { isEmpty } from 'lodash';
import { useLayoutEffect, useMemo } from 'react';
import { IntlProvider } from 'react-intl';

const MAX_WIDTH = 435;

function restictWidth(dimensions: any) {
  if (Platform.OS === 'web') {
    const width = dimensions.width;
    if (width > MAX_WIDTH) {
      return { ...dimensions, width: MAX_WIDTH };
    }
    return dimensions;
  }
}

/**
 * Devices whose only keyboard is an on-screen one. There the user cannot
 * resize the window, so a height-only viewport change is always browser
 * chrome or the keyboard - never a real layout change.
 */
const hasOnScreenKeyboard = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

const isTextEntryElement = (element: Element | null): boolean => {
  if (!element) return false;
  return (
    element.tagName === 'INPUT' ||
    element.tagName === 'TEXTAREA' ||
    (element as HTMLElement).isContentEditable === true
  );
};

type SearchQuery = {
  w?: string; // width
  target?: string; // custom-domain / embedded page id
};
export function Routes() {
  const dispatch = useDispatch();
  const windowSize = Dimensions.get('window');

  // Alert the user when the backend connection is lost (e.g. airplane mode).
  useNetworkStatus();
  const [customWidth, setCustomWidth] = useState<number | undefined>(undefined);

  const languageSetting = useSelector(appLanguageSelector);
  const currentAppLocale = AppLocale[languageSetting.locale] || AppLocale.ja;

  // Last width we applied. Ref, not state, so updating it doesn't render.
  const lastWidthRef = React.useRef<number | undefined>(windowSize?.width);

  // MIK-4798 / MIK-4805: on web the on-screen keyboard shrinks the real
  // viewport height (Android Chrome and Windows' docked keyboard resize the
  // window; iOS overlays it). That resize fed the global `dimension` store,
  // which the whole canvas is laid out against - ObjectRender scales every
  // element by dimension.height / page.height - so merely opening or closing
  // the keyboard re-flowed the entire screen. To the user that reads as the
  // app reloading: every element jumps, and the field being typed into is
  // reflowed out from under the caret.
  const isKeyboardDrivenResize = (newWindow: { width?: number }) => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') return false;

    // The keyboard only ever changes the viewport height. A width change is a
    // real layout change - rotation, window resize, breakpoint switch - and
    // must always be applied.
    if (newWindow?.width !== lastWidthRef.current) return false;

    // Phones and tablets: nothing but the keyboard and the browser's own
    // chrome can change the height, and neither should re-scale the canvas.
    // This covers the keyboard *closing* too, where the field has already been
    // blurred (MIK-4805), and taps on a button beside the field - the chat
    // element's send / attach buttons - which move focus off it mid-session.
    if (hasOnScreenKeyboard()) return true;

    // Desktop with a touch screen (MIK-4798): Windows docks its keyboard by
    // shrinking the window, but the user can also legitimately resize it, so
    // only drop the resize while a text field actually holds focus.
    return isTextEntryElement(document.activeElement);
  };

  const onChange = useCallback(
    ({ window }: any) => {
      if (!window) return;
      if (isKeyboardDrivenResize(window)) return;
      lastWidthRef.current = window.width;
      dispatch(setLoadingDimension(true));
      dispatch(
        setDimensionStore({
          ...window,
          ...(customWidth ? { width: customWidth } : {}),
        })
      );
    },
    [dispatch, customWidth]
  );

  const checkLocale = (systemLocale: string) =>
    systemLocale.includes('ja') ? 'ja' : 'en';

  useEffect(() => {
    if (windowSize) {
      lastWidthRef.current = windowSize.width;
      dispatch(
        setDimensionStore({
          ...windowSize,
          ...(customWidth ? { width: customWidth } : {}),
        })
      );
    }
  }, [customWidth]);

  useEffect(() => {
    const subscription: any = Dimensions.addEventListener('change', onChange);
    return () => {
      // react-native-web 0.16 returns nothing from addEventListener and only
      // supports the legacy removal API, so `subscription?.remove()` on its own
      // silently leaks a listener every time `onChange` changes identity -
      // leaving two live handlers dispatching a different width for one resize.
      if (subscription?.remove) subscription.remove();
      else (Dimensions as any).removeEventListener?.('change', onChange);
    };
  }, [onChange]);

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    // MIK-4799: authenticationRoute.ts can add ?target= later via
    // history.replace, which doesn't rerun index.html's class check.
    // Re-derive on every route change, not just at mount.
    const applyQueryWidth = (search: string) => {
      const query = queryString.parse(search) as SearchQuery;
      document.documentElement.classList.toggle(
        'desktop-render',
        Boolean(query.w || query.target)
      );
      if (query.w) {
        setCustomWidth(Math.min(parseInt(query.w), windowSize.width));
      } else if (query.target) {
        setCustomWidth(Math.min(desktopWidth, windowSize.width));
      }
    };

    applyQueryWidth(window.location.search);
    return history.listen((location) => applyQueryWidth(location.search));
  }, []);

  useLayoutEffect(() => {
    try {
      let locale = 'ja';

      if (Platform.OS === 'web') {
        const search = !isEmpty(window)
          ? queryString.parse(window?.location?.search)
          : {};

        if (!search.locale || typeof search.locale !== 'string') return;
        locale = search.locale;
      } else if (Platform.OS === 'ios') {
        const systemIosLocale = checkLocale(
          NativeModules.SettingsManager.settings.AppleLocale
        );

        locale = systemIosLocale;
      } else {
        locale = checkLocale(NativeModules.I18nManager.localeIdentifier);
      }

      dispatch(setLanguage(locale));
    } catch (error: any) {
      console.log('language error: ', error.message);
    }
  }, []);

  return (
    <IntlProvider
      locale={currentAppLocale?.locale || currentAppLocale}
      messages={currentAppLocale.messages}
    >
      <Suspense fallback={null}>
        <SafeAreaProvider>
          <FCMComponent>
            {customWidth ? (
              // Full-width band that horizontally centers the fixed-width content.
              // `alignItems: 'center'` centers the child even when it is wider than
              // the viewport (symmetric overflow), unlike `marginHorizontal: 'auto'`
              // which collapses to 0 as soon as the child overflows its parent.
              <View
                style={{
                  width: '100%',
                  height: '100%',
                  alignItems: 'center',
                }}
              >
                <View
                  nativeID="routes"
                  style={{
                    width: customWidth,
                    height: '100%',
                  }}
                >
                  <AppRoutes />
                </View>
              </View>
            ) : (
              <AppRoutes />
            )}
          </FCMComponent>
        </SafeAreaProvider>
      </Suspense>
    </IntlProvider>
  );
}
